/**
 * Thao tác tay của Bộ soi ảnh: lăn chuột / chụm bàn di chuột quanh con trỏ, kéo khi đã phóng,
 * chụm hai ngón, chạm giữ (kính lúp), chạm / bấm đúp (vừa khung ↔ 100%).
 *
 * Trình nghe đọc trạng thái MỚI NHẤT qua `live` (cập nhật sau mỗi lần vẽ) — không đọc biến cũ của
 * lần vẽ trước. Mốc thời gian lấy `e.timeStamp` của sự kiện, không gọi đồng hồ lúc vẽ.
 */

import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { FIT_VIEW, type ViewState } from "./types";
import {
  PINCH_K,
  panAfterDrag,
  panAroundPoint,
  scaleAfterWheel,
  VIEWER_MAX,
  WHEEL_K,
  type Pan,
  type Size,
  type Zoom,
} from "./viewport";

const LONG_PRESS_MS = 400;
const TAP_MOVE_PX = 8;
const DOUBLE_TAP_MS = 320;

export type GestureCtx = {
  view: ViewState;
  scale: number;
  fit: number;
  box: Size;
  /** Cỡ ảnh đang bày (đã xoay); vắng = chưa đo được. */
  shown: Size | undefined;
  upright: boolean;
  pageScrollWhenFit: boolean;
  setView: (v: ViewState) => void;
  /** Chạm / bấm một lần. Trả true nếu đã dùng (ống hút ghim màu) ⇒ không tính chạm đúp. */
  onTap: (pt: { x: number; y: number }) => boolean;
};

export type Hover = { x: number; y: number };
type Pt = { x: number; y: number };

export function useViewerGestures(rootRef: RefObject<HTMLElement | null>, ctx: GestureCtx) {
  const live = useRef(ctx);
  useLayoutEffect(() => {
    live.current = ctx;
  });
  const [hover, setHover] = useState<Hover | null>(null);
  const [longPress, setLongPress] = useState(false);
  const pointers = useRef(new Map<number, Pt>());
  const gesture = useRef<{
    start: Pt;
    moved: boolean;
    pinch?: { dist: number; scale: number };
    timer?: ReturnType<typeof setTimeout>;
  } | null>(null);
  const lastTap = useRef<{ t: number; pt: Pt } | null>(null);

  const zoomTo = (z: Zoom, pt?: Pt) => {
    const s = live.current;
    if (!s.shown) return;
    if (z === "fit") return s.setView(FIT_VIEW);
    const at = pt ?? { x: s.box.w / 2, y: s.box.h / 2 };
    s.setView({ zoom: z, pan: panAroundPoint({ scale: s.scale, pan: s.view.pan }, z, at, s.box, s.shown) });
  };
  const zoomRef = useRef(zoomTo);
  useLayoutEffect(() => {
    zoomRef.current = zoomTo;
  });

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const onWheel = (e: WheelEvent) => {
      const s = live.current;
      // Lăn trên thanh nút / hộp công cụ thì không phóng.
      const el = (e.target as Element | null)?.closest?.("[data-pv-stage]");
      if (!s.shown || !el) return;
      if (s.pageScrollWhenFit && s.view.zoom === "fit" && !e.ctrlKey) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const z = scaleAfterWheel(s.scale, e.deltaY, s.fit, e.ctrlKey ? PINCH_K : WHEEL_K, VIEWER_MAX);
      zoomRef.current(z, { x: e.clientX - r.left, y: e.clientY - r.top });
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    return () => root.removeEventListener("wheel", onWheel);
  }, [rootRef]);

  const local = (e: React.PointerEvent): Pt => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const two = (): [Pt, Pt] => [...pointers.current.values()].slice(0, 2) as [Pt, Pt];

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const pt = local(e);
    pointers.current.set(e.pointerId, pt);
    if (gesture.current?.timer) clearTimeout(gesture.current.timer);
    if (pointers.current.size === 2) {
      const [a, b] = two();
      gesture.current = { start: pt, moved: true, pinch: { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, scale: live.current.scale } };
      return;
    }
    const g: NonNullable<typeof gesture.current> = { start: pt, moved: false };
    if (e.pointerType !== "mouse" && live.current.upright) {
      g.timer = setTimeout(() => {
        if (gesture.current === g && !g.moved) {
          setLongPress(true);
          setHover(pt);
        }
      }, LONG_PRESS_MS);
    }
    gesture.current = g;
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const pt = local(e);
    if (e.pointerType === "mouse" || longPress) setHover(pt);
    const prev = pointers.current.get(e.pointerId);
    const g = gesture.current;
    if (!prev || !g) return;
    pointers.current.set(e.pointerId, pt);
    const s = live.current;
    if (g.pinch && pointers.current.size >= 2 && s.shown) {
      const [a, b] = two();
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const next = Math.min(VIEWER_MAX, (g.pinch.scale * dist) / g.pinch.dist);
      if (next <= s.fit * 1.01) return s.setView(FIT_VIEW);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      return s.setView({ zoom: next, pan: panAroundPoint({ scale: s.scale, pan: s.view.pan }, next, mid, s.box, s.shown) });
    }
    if (Math.hypot(pt.x - g.start.x, pt.y - g.start.y) > TAP_MOVE_PX) {
      g.moved = true;
      if (g.timer) clearTimeout(g.timer);
    }
    if (longPress || !g.moved || s.view.zoom === "fit" || !s.shown) return;
    const pan: Pan = panAfterDrag(s.view.pan, pt.x - prev.x, pt.y - prev.y, s.shown.w * s.scale, s.shown.h * s.scale);
    s.setView({ zoom: s.view.zoom, pan });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const pt = local(e);
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (g?.timer) clearTimeout(g.timer);
    if (longPress) {
      setLongPress(false);
      if (e.pointerType !== "mouse") setHover(null);
    }
    if (pointers.current.size > 0) return;
    gesture.current = null;
    if (!g || g.moved || g.pinch) return;
    if (live.current.onTap(pt)) return;
    const last = lastTap.current;
    if (last && e.timeStamp - last.t < DOUBLE_TAP_MS && Math.hypot(pt.x - last.pt.x, pt.y - last.pt.y) < 24) {
      lastTap.current = null;
      zoomTo(live.current.view.zoom === "fit" ? 1 : "fit", pt);
    } else {
      lastTap.current = { t: e.timeStamp, pt };
    }
  };

  const onPointerCancel = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (gesture.current?.timer) clearTimeout(gesture.current.timer);
    gesture.current = null;
    setLongPress(false);
  };

  const onPointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType === "mouse") setHover(null);
  };

  return {
    hover,
    longPress,
    zoomTo,
    stageHandlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave },
  };
}
